import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { courseSessionsService } from './courseSessionsService';

export type InterpreterRow = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  language1: string | null;
  language2: string | null;
  language3: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

function mapRow(r: {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  language_1: string | null;
  language_2: string | null;
  language_3: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}): InterpreterRow {
  return {
    id: r.id,
    name: r.name,
    email: r.email,
    phone: r.phone,
    language1: r.language_1,
    language2: r.language_2,
    language3: r.language_3,
    isActive: r.is_active,
    createdAt: r.created_at,
    updatedAt: r.updated_at
  };
}

export const interpretersService = {
  async getByEmail(email: string): Promise<InterpreterRow | null> {
    if (!isSupabaseConfigured()) return null;
    const normalized = email.trim().toLowerCase();
    const { data, error } = await supabase
      .from('interpreters')
      .select('*')
      .eq('is_active', true)
      .eq('email', normalized)
      .maybeSingle();
    if (error) throw error;
    return data ? mapRow(data as any) : null;
  },

  async listActive(): Promise<InterpreterRow[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('interpreters')
      .select('*')
      .eq('is_active', true)
      .order('name');
    if (error) throw error;
    return (data || []).map((r: any) => mapRow(r));
  },

  async listAll(): Promise<InterpreterRow[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase.from('interpreters').select('*').order('name');
    if (error) throw error;
    return (data || []).map((r: any) => mapRow(r));
  },

  async getByIds(ids: string[]): Promise<Record<string, InterpreterRow>> {
    const uniq = [...new Set(ids.filter(Boolean))];
    if (!uniq.length || !isSupabaseConfigured()) return {};
    const { data, error } = await supabase.from('interpreters').select('*').in('id', uniq);
    if (error) throw error;
    const map: Record<string, InterpreterRow> = {};
    (data || []).forEach((r: any) => {
      map[r.id] = mapRow(r);
    });
    return map;
  },

  async create(input: { name: string; email?: string | null; phone?: string | null; language1?: string | null; language2?: string | null; language3?: string | null; isActive?: boolean }): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const rowWithLanguages = {
      name: input.name.trim(),
      email: input.email?.trim().toLowerCase() || null,
      phone: input.phone?.trim() || null,
      language_1: input.language1?.trim() || null,
      language_2: input.language2?.trim() || null,
      language_3: input.language3?.trim() || null,
      is_active: input.isActive !== false,
      updated_at: new Date().toISOString()
    };
    let { error } = await supabase.from('interpreters').insert(rowWithLanguages);
    if (error && String((error as any)?.message || '').toLowerCase().includes('language_')) {
      const retry = await supabase.from('interpreters').insert({
        name: input.name.trim(),
        email: input.email?.trim().toLowerCase() || null,
        phone: input.phone?.trim() || null,
        is_active: input.isActive !== false,
        updated_at: new Date().toISOString()
      });
      error = retry.error;
    }
    if (error) throw error;
  },

  async update(
    id: string,
    patch: { name?: string; email?: string | null; phone?: string | null; language1?: string | null; language2?: string | null; language3?: string | null; isActive?: boolean }
  ): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.name !== undefined) row.name = patch.name.trim();
    if (patch.email !== undefined) row.email = patch.email?.trim().toLowerCase() || null;
    if (patch.phone !== undefined) row.phone = patch.phone?.trim() || null;
    if (patch.language1 !== undefined) row.language_1 = patch.language1?.trim() || null;
    if (patch.language2 !== undefined) row.language_2 = patch.language2?.trim() || null;
    if (patch.language3 !== undefined) row.language_3 = patch.language3?.trim() || null;
    if (patch.isActive !== undefined) row.is_active = patch.isActive;
    let { error } = await supabase.from('interpreters').update(row).eq('id', id);
    if (error && String((error as any)?.message || '').toLowerCase().includes('language_')) {
      const fallback: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (patch.name !== undefined) fallback.name = patch.name.trim();
      if (patch.email !== undefined) fallback.email = patch.email?.trim().toLowerCase() || null;
      if (patch.phone !== undefined) fallback.phone = patch.phone?.trim() || null;
      if (patch.isActive !== undefined) fallback.is_active = patch.isActive;
      const retry = await supabase.from('interpreters').update(fallback).eq('id', id);
      error = retry.error;
    }
    if (error) throw error;
  },

  async remove(id: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    await courseSessionsService.clearInterpreterFromSessions(id);
    const { error } = await supabase.from('interpreters').delete().eq('id', id);
    if (error) throw error;
  },

  /** Lightweight rows for global search. */
  async searchForGlobal(
    term: string,
    limit: number = 15
  ): Promise<Array<{ id: string; name: string; email: string | null; phone: string | null; language1: string | null; language2: string | null; language3: string | null }>> {
    if (!isSupabaseConfigured()) return [];
    const safe = term.trim().replace(/,/g, ' ');
    if (!safe) return [];
    const uuidLike =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(safe);
    const orBase = `name.ilike.%${safe}%,email.ilike.%${safe}%,phone.ilike.%${safe}%,language_1.ilike.%${safe}%,language_2.ilike.%${safe}%,language_3.ilike.%${safe}%`;
    const orClause = uuidLike ? `${orBase},id.eq.${safe}` : orBase;
    const { data, error } = await supabase
      .from('interpreters')
      .select('id, name, email, phone, language_1, language_2, language_3')
      .or(orClause)
      .order('name')
      .limit(limit);
    if (error) {
      console.error('interpretersService.searchForGlobal', error);
      return [];
    }
    return (data || []).map((r: Record<string, unknown>) => ({
      id: String(r.id),
      name: String(r.name || ''),
      email: r.email ? String(r.email) : null,
      phone: r.phone ? String(r.phone) : null,
      language1: r.language_1 ? String(r.language_1) : null,
      language2: r.language_2 ? String(r.language_2) : null,
      language3: r.language_3 ? String(r.language_3) : null,
    }));
  },
};
